/**
 * Feature flags for Home Approach.
 *
 * OPEN_BETA_MODE
 *   When true, ALL visitors (signed in or anonymous) receive full access to
 *   premium tools, AI insights, quiz results, and recorded webinars
 *   (hasPremium = true) regardless of their actual tier. The VipUpgradeBanner
 *   is suppressed. Cashouts in the Points Store remain milestone-gated
 *   (hasVipPro is NOT overridden) and Admin/EmployerAdmin RBAC is unchanged.
 *
 *   Set to `false` to restore the original VIP-only experience. The underlying
 *   tier computation (free / vip / vip_pro) and vipProgress remain accurate
 *   while open beta is on, so re-gating later requires no other code changes.
 *   `realHasPremium` from usePremiumAccess is the frame-of-reference value
 *   that reflects what each user WOULD see if the gates were re-enabled today.
 */
export const OPEN_BETA_MODE = true;

/**
 * WEBINARS_ENABLED
 *   When false, all user-facing webinar surfaces are hidden:
 *   nav entries, footer link, marketing CTAs, dashboard shelves, and the
 *   /webinars and /webinars/:id routes (which redirect to /learn).
 *   Admin tooling at /admin/webinars, the useWebinars hook, edge functions,
 *   and DB tables remain intact so flipping this back to true restores the
 *   feature without further code changes.
 */
export const WEBINARS_ENABLED = false;

/**
 * LIFEMART_PUBLIC_PAGES_ENABLED
 *   Revised 2026-08-06 (twice, same day). Originally (2026-08-05) a single
 *   kill switch for five LifeMart-adjacent public pages; /lifemart,
 *   /lifemart/access, /lifemart/survey, and /lifemart/stack-invite were
 *   restored to unconditionally live the same day (real recent activity
 *   observed on access/survey; none of these were ever newly exposed, only
 *   reverted to their pre-2026-08-05 state) and no longer read this flag.
 *
 *   This flag now controls only /free-credit-score (public, top-level)
 *   and /lifemart/stack-offer (a re-engagement-email landing page --
 *   src/lib/adminReengage.server.ts still builds email CTAs pointing here;
 *   with this gated, clicking that link redirects home instead of landing
 *   on the offer -- the email-send logic itself is unaffected). Both
 *   redirect to "/" when false.
 *
 *   /lifemart/free-credit-score was briefly gated earlier the same day,
 *   then explicitly reverted back to unconditionally live -- it no longer
 *   reads this flag at all and is not the same page as /lifemart/stack,
 *   which was never gated. The rest of the LifeMart co-brand experience
 *   (dashboard integration, /lifemart/stack, /lifemart/hook-preview --
 *   already noindexed in RouteMetaGuard) is untouched.
 *
 *   Whether /lifemart should redirect to /lifemart/stack, be removed
 *   entirely, or /lifemart/stack should be renamed to a shorter /stack
 *   URL are open questions pending stakeholder input, not yet decided.
 */
export const LIFEMART_PUBLIC_PAGES_ENABLED = false;

/**
 * CREDIT_SCORE_PUBLIC_PAGE_ENABLED
 *   Split out of LIFEMART_PUBLIC_PAGES_ENABLED on 2026-08-27. /free-credit-score
 *   is the public $3 credit-score hook and is not a LifeMart page — it was only
 *   dark because it shared that flag. LifeMart's own public pages stay gated by
 *   LIFEMART_PUBLIC_PAGES_ENABLED; this controls /free-credit-score alone.
 */
export const CREDIT_SCORE_PUBLIC_PAGE_ENABLED = true;

/**
 * TWO_CURRENCY_UI
 *   When true, the dashboard, header, points store, /hero-rewards page, and
 *   admin tools render the two-currency split: Approach XP (engagement,
 *   non-redeemable) and Cash Credits (milestone-only, redeemable).
 *
 *   When false (default on `main`), the original single Approach Points UI
 *   is rendered unchanged. The underlying database is forward-compatible
 *   either way — gating lives in `public.app_settings.two_currency_enabled`
 *   on the DB side and `VITE_TWO_CURRENCY_UI` on the client.
 */
export const TWO_CURRENCY_UI =
  import.meta.env.VITE_TWO_CURRENCY_UI === "true";

/**
 * CREDIT_KARMA_OFFER_ENABLED
 *   Turned off 2026-10-16 so Home Approach can truthfully say employers are
 *   its only source of pay. When false: no Credit Karma tracked links render,
 *   the /free-credit-score, /check-my-score, /lifemart/free-credit-score and
 *   /onboarding/credit-match funnels redirect to /credit-readiness, and no
 *   Credit Karma offer emails go out. Already-earned rewards are still paid.
 */
export const CREDIT_KARMA_OFFER_ENABLED = false;

/**
 * EMPLOYER_REFERRAL_PROGRAM_ENABLED
 *   Member "refer your employer" revenue-share program. Paused 2026-11-05
 *   pending legal/tax review: page redirects to /request-access and all
 *   member/admin referral surfaces are hidden. Records and payout math stay.
 */
export const EMPLOYER_REFERRAL_PROGRAM_ENABLED = false;

/**
 * B2C_PAGES_ENABLED
 *   Consumer-only pages hidden after the employer-only pivot (2026-11-05).
 *   When false, every path in HIDDEN_B2C_PATHS (and its subpaths) redirects
 *   to /request-access from the root route.
 */
export const B2C_PAGES_ENABLED = false;

export const HIDDEN_B2C_PATHS = [
  "/rewards", "/points-store", "/leaderboard", "/contests", "/hero-rewards",
  "/founding-members", "/share-progress", "/offers",
  "/free-credit-score", "/check-my-score",
  "/your-match", "/focus-group", "/wellness-survey", "/pulse", "/success-stories", "/install",
  "/webinars",
  "/experts", "/expert-qa", "/expert-auth", "/expert-dashboard",
  "/lender-match", "/approved-lenders",
  "/refer-your-employer",
] as const;

export function isHiddenB2CPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return HIDDEN_B2C_PATHS.some((h) => {
    if (h === "/refer-your-employer") {
      return !EMPLOYER_REFERRAL_PROGRAM_ENABLED && (p === h || p.startsWith(h + "/"));
    }
    return !B2C_PAGES_ENABLED && (p === h || p.startsWith(h + "/"));
  });
}
